import React from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { User } from '~/components/Shared/User/User';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { User as EdsUser } from '~/eds/src/types/User';
import { TicketStageType } from '~/enums/TicketStageType';
import { PilotId } from '~/types/PilotId';

export const getUnassignedUserDisplay = (
  stage: TicketStageType,
  intl: IntlShape,
) => {
  switch (stage) {
    case TicketStageType.Review:
      return intl.formatMessage({
        id: 'UserInfo.unassignedReviewer',
        defaultMessage: 'Unassigned Reviewer',
        description: 'Label for unassigned reviewer',
      });
    case TicketStageType.Sign:
      return intl.formatMessage({
        id: 'UserInfo.unassignedSigner',
        defaultMessage: 'Unassigned Signer',
        description: 'Label for unassigned signer',
      });
    case TicketStageType.Finalize:
      return intl.formatMessage({
        id: 'UserInfo.unassignedFinalizer',
        defaultMessage: 'Unassigned Finalizer',
        description: 'Label for unassigned finalizer',
      });
    default:
      return intl.formatMessage({
        id: 'UserInfo.unassignedUser',
        defaultMessage: 'Unassigned User',
        description: 'Label for unassigned user',
      });
  }
};

export const unassignedCoordinator = 'Unassigned Coordinator';

interface Props {
  userId?: PilotId;
  externalUser: Pick<EdsUser, 'email' | 'firstName' | 'lastName' | 'id'>;
  currentStage: TicketStageType;
  isCoordinator?: boolean;
  isCounterpartySigner?: boolean;
}

export function UserInfo({
  isCounterpartySigner = false,
  userId,
  externalUser,
  currentStage,
  isCoordinator = false,
}: Props) {
  const intl = useIntl();

  const unassignedCoordinator = intl.formatMessage({
    id: 'UserInfo.unassignedCoordinator',
    defaultMessage: 'Unassigned Coordinator',
    description: 'Label for unassigned coordinator',
  });

  return (
    <Layout align="center" spacing={4}>
      <Layout align="center" spacing={2}>
        <User
          mode="info"
          id={userId}
          user={externalUser}
          placeholder={
            isCoordinator
              ? unassignedCoordinator
              : getUnassignedUserDisplay(currentStage, intl)
          }
        />
        {isCounterpartySigner && (
          <Chip
            text={intl.formatMessage({
              id: 'UserInfo.counterpartySignerChip',
              defaultMessage: 'Counterparty Signer',
              description: 'Chip text for counterparty signer',
            })}
          />
        )}
      </Layout>
    </Layout>
  );
}
